import { getPlatformStocksOptions, useProducts } from '@gozoki/api'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { FileSpreadsheet, Layers, Search } from 'lucide-react'
import { useState } from 'react'
import z from 'zod'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { Title } from '~/components/new/app-ui/title'
import { withResourcePermission } from '~/components/RequireResourcePermission'
import {
  DESSERT_REAPPRO_CATEGORY_ID,
  OSAKA_STORE_REF,
} from '~/components/routes/platform-stocks/constants'
import { PlatformStockDeclarationDialog } from '~/components/routes/platform-stocks/declaration/platform-stock-declaration-dialog'
import { PlatformStockBulkDialog } from '~/components/routes/platform-stocks/PlatformStockBulkDialog'
import { PlatformStockTable } from '~/components/routes/platform-stocks/stock/platform-stock-table'
import { Button } from '~/components/ui/button'
import {
  Combobox,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxPopup,
} from '~/components/ui/combobox'

export const Route = createFileRoute('/_authenticated/platform-stocks/')({
  component: withResourcePermission('platform-stocks', 'read')(PlatformStocksPage),
  validateSearch: z.object({
    productReference: z.string().optional(),
  }),
})

interface ProductItem {
  label: string
  reference: string
}

function PlatformStocksPage() {
  const navigate = Route.useNavigate()
  const { productReference } = Route.useSearch()
  const [bulkOpen, setBulkOpen] = useState<boolean>(false)
  const [lotSearch, setLotSearch] = useState<string>('')

  const { data: allProducts = [] } = useProducts({ reapproCategoryId: DESSERT_REAPPRO_CATEGORY_ID })
  const dessertProducts: ProductItem[] = allProducts.map((p) => ({
    label: p.label,
    reference: p.reference,
  }))

  const selectedProduct = dessertProducts.find((p) => p.reference === productReference) ?? null

  const { data: stocksData, isLoading } = useQuery(
    getPlatformStocksOptions({
      storeReference: OSAKA_STORE_REF,
      pageSize: 1000,
    }),
  )

  const allStocks = stocksData?.data ?? []
  const stocks = productReference
    ? allStocks
        .filter((s) => s.productReference === productReference)
        .filter((s) => !lotSearch || s.lot.toLowerCase().includes(lotSearch.toLowerCase()))
    : []

  const stockTotal = stocks.reduce((sum, s) => sum + s.quantity, 0)

  return (
    <>
      <AppHeader>
        <Title>Stock Plateforme</Title>
      </AppHeader>

      <AppContainer>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Combobox
              items={dessertProducts}
              value={selectedProduct}
              onValueChange={(item: ProductItem | null) =>
                navigate({
                  search: (prev) => ({
                    ...prev,
                    productReference: item?.reference ?? undefined,
                  }),
                })
              }
            >
              <ComboboxInput
                placeholder="Sélectionner un produit"
                onFocus={(e) => e.target.select()}
                className="w-80"
              />
              <ComboboxPopup>
                <ComboboxEmpty>Aucun produit trouvé</ComboboxEmpty>
                <ComboboxList>
                  {(item: ProductItem) => (
                    <ComboboxItem key={item.reference} value={item}>
                      {item.label}{' '}
                      <span className="ml-1 font-mono text-xs text-muted-foreground">
                        {item.reference}
                      </span>
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxPopup>
            </Combobox>

            {productReference && (
              <div className="relative w-52">
                <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={lotSearch}
                  onChange={(e) => setLotSearch(e.target.value)}
                  placeholder="Rechercher un lot..."
                  className="w-full rounded-md border bg-white py-1.5 pl-8 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring placeholder:text-muted-foreground/50"
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setBulkOpen(true)}>
              <FileSpreadsheet className="size-4" />
              Import / Export
            </Button>
            <PlatformStockDeclarationDialog
              products={dessertProducts}
              productReference={productReference}
            />
          </div>
        </div>

        {selectedProduct && (
          <div className="flex items-center gap-2 text-sm">
            <span className="font-semibold text-foreground">{selectedProduct.label}</span>
            <span className="text-muted-foreground">·</span>
            <code className="rounded border bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
              {selectedProduct.reference}
            </code>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">
              Stock total : <span className="font-semibold text-foreground">{stockTotal}</span>{' '}
              unités
            </span>
          </div>
        )}

        {!productReference ? (
          <div className="flex flex-col items-center justify-center gap-3 py-24 text-muted-foreground">
            <Layers className="size-10 opacity-30" />
            <p className="text-sm">Sélectionnez un produit pour afficher les stocks</p>
          </div>
        ) : (
          <PlatformStockTable stocks={stocks} isLoading={isLoading} />
        )}

        <PlatformStockBulkDialog open={bulkOpen} onOpenChange={setBulkOpen} />
      </AppContainer>
    </>
  )
}
